import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Shell from "../components/Shell";
import { useAuth } from "../context/AuthContext";
import { Target, Phone, Users, Map } from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ label, value, sub, testid, accent, negative }) => (
  <div className="border border-term p-4 sm:p-5 rounded-md" style={{ background: "var(--bg-raised)" }} data-testid={testid}>
    <div className="label-xs">{label}</div>
    <div className={`font-display text-2xl sm:text-3xl md:text-4xl mt-2 leading-none ${accent || ""} ${negative ? "text-live" : ""}`}>{value}</div>
    {sub && <div className="text-[10px] text-fg-muted mt-2 font-mono uppercase tracking-widest">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [kpi, setKpi] = useState(null);

  useEffect(() => { api.get("/dashboard/kpis").then((r) => setKpi(r.data)); }, []);

  if (!kpi) return <Shell><div className="p-12 label-xs">Loading KPIs...</div></Shell>;

  const name = user?.full_name?.split(" ")[0] || "Agent";

  return (
    <Shell>
      <div className="p-4 sm:p-8 max-w-7xl" data-testid="dashboard-page">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="label-xs text-amber">&gt; AGENT_CONTROL_ROOM</div>
            <h1 className="font-display text-4xl sm:text-5xl leading-none mt-2">
              Morning, {name}<span className="blink text-amber">_</span>
            </h1>
          </div>
          <div className="flex items-center gap-2 text-[10px] font-mono text-gain uppercase tracking-widest">
            <span className="w-1.5 h-1.5 bg-gain rounded-full pulse-live"></span>
            LIVE · {new Date().toISOString().slice(0, 10)}
          </div>
        </div>

        {/* Income goal */}
        <div className="border border-term p-5 sm:p-8 mb-6 rounded-lg relative overflow-hidden" style={{ background: "linear-gradient(135deg, var(--bg-raised) 0%, var(--bg-subtle) 100%)" }} data-testid="goal-tracker">
          <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(ellipse at top right, rgba(77,158,255,0.15), transparent 60%)" }}></div>
          <div className="relative flex items-center justify-between flex-wrap gap-6">
            <div>
              <div className="label-xs flex items-center gap-2 text-amber"><Target size={12} /> INCOME_GOAL_TRACKER</div>
              <div className="flex items-baseline gap-3 mt-3 flex-wrap">
                <div className="font-display text-4xl sm:text-6xl text-amber leading-none">${kpi.commission_earned.toLocaleString()}</div>
                <div className="text-fg-muted font-mono text-xs sm:text-sm">/ ${Number(kpi.income_goal).toLocaleString()}</div>
              </div>
              <div className="text-xs text-fg-muted mt-2 font-mono uppercase tracking-widest">
                <span className="text-electric">{kpi.goal_progress_pct}%</span> · {kpi.sales_needed_to_goal} SALES TO GOAL
              </div>
            </div>
            <Link to="/goals" className="btn-secondary relative z-10" data-testid="edit-goal-btn">Adjust &gt;</Link>
          </div>
          <div className="h-1.5 bg-[var(--bg-muted)] mt-6 rounded-full overflow-hidden relative">
            <div className="h-full rounded-full transition-all" style={{ width: `${kpi.goal_progress_pct}%`, background: "linear-gradient(90deg, var(--amber), var(--electric))" }} />
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <Stat testid="kpi-dials" label="Total Dials" value={kpi.total_dials} sub={`${kpi.connect_rate}% connect`} accent="text-electric" />
          <Stat testid="kpi-leads" label="Active Leads" value={kpi.total_leads} sub={`${kpi.closed_won} won · ${kpi.closed_lost} lost`} />
          <Stat testid="kpi-conv" label="Conv. Rate" value={`${kpi.conversion_rate}%`} sub="lead → sale" accent={kpi.conversion_rate >= 15 ? "text-gain" : "text-electric"} />
          <Stat testid="kpi-ai" label="AI Score" value={kpi.avg_ai_score || "—"} sub="call quality" accent={kpi.avg_ai_score >= 70 ? "text-gain" : kpi.avg_ai_score >= 40 ? "text-warn" : "text-fg-muted"} />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Stat testid="kpi-cpl" label="Cost / Lead" value={`$${kpi.cost_per_lead}`} />
          <Stat testid="kpi-dpd" label="$ / Dial" value={`$${kpi.dollar_per_dial}`} accent="text-amber" />
          <Stat testid="kpi-comm" label="Commission" value={`$${Math.round(kpi.commission_earned).toLocaleString()}`} sub={`pending $${Math.round(kpi.commission_pending).toLocaleString()}`} accent="text-gain" />
          <Stat testid="kpi-onboard" label="Onboarding" value={`${kpi.onboarding_pct}%`} sub={`${kpi.onboarding_done}/${kpi.onboarding_total} done`} accent="text-electric" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Link to="/onboarding" className="tap-card p-6 block" data-testid="action-onboarding">
            <Map size={20} strokeWidth={1.5} className="text-amber" />
            <div className="font-display text-xl mt-4 text-fg">Onboarding &gt;</div>
            <div className="text-xs text-fg-muted mt-1 font-mono uppercase tracking-widest">{kpi.onboarding_done}/{kpi.onboarding_total} STEPS</div>
          </Link>
          <Link to="/dialer" className="tap-card p-6 block" data-testid="action-dialer">
            <Phone size={20} strokeWidth={1.5} className="text-electric" />
            <div className="font-display text-xl mt-4 text-fg">Start Dialing &gt;</div>
            <div className="text-xs text-fg-muted mt-1 font-mono uppercase tracking-widest">AI COACH ACTIVE</div>
          </Link>
          <Link to="/war-room" className="tap-card p-6 block" data-testid="action-warroom">
            <Users size={20} strokeWidth={1.5} className="text-live" />
            <div className="font-display text-xl mt-4 text-fg">War Room &gt;</div>
            <div className="text-xs text-fg-muted mt-1 font-mono uppercase tracking-widest flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-live rounded-full pulse-live"></span> LIVE
            </div>
          </Link>
        </div>
      </div>
    </Shell>
  );
}
